import { FormattedMessage, useIntl } from "react-intl";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { ImportMode } from "@/graphql/generated/types";
import type { ImportRunOptions } from "./import-run-options";

/**
 * The two import options, as two independent choices.
 *
 * `dryRun` and `mode` are orthogonal in the backend contract (all four
 * combinations are legal), so they are two controls rather than one four-way
 * list: collapsing them would invent a coupling the importer does not have.
 *
 * Both are offered for the YAML route only. PDF/DOCX go to the extraction
 * pipeline, which supports the defaults exclusively, so the caller unmounts this
 * whole block instead of rendering disabled controls for it.
 */
export function ImportOptionsPicker({
  value,
  onChange,
  disabled = false,
}: {
  value: ImportRunOptions;
  onChange: (next: ImportRunOptions) => void;
  disabled?: boolean;
}) {
  const intl = useIntl();

  return (
    <div className="mt-4 mb-6 grid grid-cols-1 gap-4 @lg:grid-cols-2 @lg:gap-x-10 @lg:gap-y-3">
      <div className="min-w-0 space-y-1.5">
        <span className="smycles-field-meta block text-ink">
          <FormattedMessage id="service.page.bulkYaml.actionLabel" defaultMessage="Mode" />
        </span>
        <SegmentedControl
          id="import-mode"
          aria-label={intl.formatMessage({
            id: "service.page.bulkYaml.actionLabel",
            defaultMessage: "Mode",
          })}
          size="kpi"
          value={value.dryRun ? "dry" : "real"}
          onChange={(next) => onChange({ ...value, dryRun: next === "dry" })}
          // Validation first, import second: the order reads as the sequence an
          // operator is meant to follow, and the safe option is the one the eye
          // lands on first.
          options={[
            {
              value: "dry",
              label: <FormattedMessage id="service.page.bulkYaml.actionDry" defaultMessage="Validate" />,
              disabled,
            },
            {
              value: "real",
              label: <FormattedMessage id="service.page.bulkYaml.actionReal" defaultMessage="Import" />,
              disabled,
            },
          ]}
        />
      </div>

      <div className="min-w-0 space-y-1.5">
        <span className="smycles-field-meta block text-ink">
          <FormattedMessage id="service.page.bulkYaml.modeLabel" defaultMessage="On errors" />
        </span>
        <SegmentedControl
          id="import-on-error"
          aria-label={intl.formatMessage({
            id: "service.page.bulkYaml.modeLabel",
            defaultMessage: "On errors",
          })}
          size="kpi"
          value={value.mode}
          onChange={(mode) => onChange({ ...value, mode })}
          options={[
            {
              value: ImportMode.AllOrNothing,
              label: (
                <FormattedMessage
                  id="service.page.bulkYaml.modeAllOrNothing"
                  defaultMessage="Abort completely"
                />
              ),
              disabled,
            },
            {
              value: ImportMode.KeepSuccessful,
              label: (
                <FormattedMessage
                  id="service.page.bulkYaml.modeKeepSuccessful"
                  defaultMessage="Keep successful"
                />
              ),
              disabled,
            },
          ]}
        />
      </div>

      {/* The one asymmetry an operator cannot infer from the two labels: a
          validation run is one single transaction even in KEEP_SUCCESSFUL,
          because the importer has no savepoints. Saying so here is cheaper than
          letting someone expect per-unit isolation and be surprised by an
          aborted run. */}
      {value.dryRun ? (
        <p id="import-dryrun-note" className="text-smycles-label text-ink-muted @lg:col-span-2">
          <FormattedMessage
            id="service.page.bulkYaml.dryRunAtomicNote"
            defaultMessage="A validation always covers the whole file at once. Single units are not isolated here."
          />
        </p>
      ) : null}
    </div>
  );
}
